import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, Radio, Alert, Tooltip, Tag, Divider } from 'antd';
import classNames from 'classnames';

import {
  PERMISSIONS_MODULES,
  PERMISSIONS_OPTIONS_VALUE
} from 'constants/team-members';
import {
  fillRestOfPermissionsBySpecificAccess,
  isAllPermissionsSelected,
  hasNoSelectedPermissions
} from 'utils/team-members';
import { mediaHook } from 'customHooks';

import { ReactComponent as ErrorAlert } from 'assets/bosta-icons/alert-triangle.svg';

import './PermissionsSection.less';

const { FULL_ACCESS, EDITOR, VIEW_ONLY, NO_ACCESS } = PERMISSIONS_OPTIONS_VALUE;

const PermissionsSection = ({
  selectedPermissions,
  setSelectedPermissions,
  isFieldsTouched,
  setIsFieldsTouched,
  isPrimaryBtnDisabled,
  setIsPrimaryBtnDisabled,
  isEdit,
  mobileScreenSizes: { isMediumMobileScreen }
}) => {
  const { formatMessage } = useIntl();

  const [isAllSelected, setIsAllSelected] = useState(false);

  const accessRadioBtns = [
    {
      title: formatMessage({
        id: 'team_members.create_edit_member.permissions_section.columns.full_access'
      }),
      access: FULL_ACCESS
    },
    {
      title: formatMessage({
        id: 'team_members.create_edit_member.permissions_section.columns.editor'
      }),
      access: EDITOR
    },
    {
      title: formatMessage({
        id: 'team_members.create_edit_member.permissions_section.columns.view_only'
      }),
      access: VIEW_ONLY
    }
  ];

  const {
    user: { isOrganizationalAccount }
  } = JSON.parse(localStorage.getItem('userInfo'));

  const handleChangeFlagsForEdit = () => {
    if (!isFieldsTouched) {
      setIsFieldsTouched(true);
    }

    if (isEdit && isPrimaryBtnDisabled) {
      setIsPrimaryBtnDisabled(false);
    }
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    let allPermissions = { ...selectedPermissions };

    if (isChecked) {
      allPermissions = fillRestOfPermissionsBySpecificAccess({
        permissions: selectedPermissions
      });
    } else {
      allPermissions = {};
    }

    handleChangeFlagsForEdit();
    setSelectedPermissions(allPermissions);
  };

  const handleSelectItem = ({ key, checked, defaultAccess }) => {
    handleChangeFlagsForEdit();

    setSelectedPermissions((prev) => {
      const permissions = { ...prev };

      if (checked) {
        permissions[key] = defaultAccess;
      } else {
        delete permissions[key];
      }

      return permissions;
    });
  };

  const handleSelectPermissionType = ({ key, option }) => {
    handleChangeFlagsForEdit();
    setSelectedPermissions((prev) => ({
      ...prev,
      [key]: option
    }));
  };

  const renderItem = ({
    apiKey,
    icon,
    title,
    description,
    defaultAccess,
    fullAccessTooltip
  }) => {
    const selectedOption = selectedPermissions[apiKey];
    const isSelected = !!(selectedOption && selectedOption !== NO_ACCESS);
    const hasFullAccess = selectedOption === FULL_ACCESS;

    return (
      <div
        key={apiKey}
        className={classNames('br-permissions-section__table__row', {
          selected: isSelected
        })}
      >
        <div className="br-permissions-section__row__checkbox">
          <Checkbox
            onChange={(e) =>
              handleSelectItem({
                key: apiKey,
                checked: e.target.checked,
                defaultAccess
              })
            }
            checked={isSelected}
          />
          <div className="option-item br-permissions-section__row__name">
            {icon}
            <div>
              <div className="br-permissions-section__row__title">
                <span className="body-medium">{title}</span>
                {fullAccessTooltip &&
                  hasFullAccess &&
                  !isOrganizationalAccount && (
                    <Tooltip
                      overlayClassName="br-permissions-section__tooltip"
                      title={
                        <>
                          <div>
                            {formatMessage({
                              id: 'team_members.create_edit_member.permissions_section.be_carful'
                            })}
                          </div>
                          <Divider className="tooltip-divider" />
                          {fullAccessTooltip}
                        </>
                      }
                    >
                      <Tag className="br-permissions-section__tag alert">
                        <span>
                          {formatMessage({
                            id: 'common.caution'
                          })}
                        </span>
                        <ErrorAlert />
                      </Tag>
                    </Tooltip>
                  )}
              </div>
              {selectedOption && (
                <span className="br-permissions-section__description">
                  {description[selectedOption]}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="br-permissions-section__table__options">
          <Radio.Group
            value={selectedOption}
            onChange={(e) =>
              handleSelectPermissionType({
                option: e.target.value,
                key: apiKey
              })
            }
          >
            {accessRadioBtns.map(({ title, access }) => (
              <Radio
                value={access}
                disabled={!isSelected || !description[access]}
              >
                {isMediumMobileScreen && title}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setIsAllSelected(isAllPermissionsSelected(selectedPermissions));
  }, [selectedPermissions]);

  return (
    <div className="br-permissions-section br-create-edit-page-card">
      <div className="br-create-edit-page-card__title">
        {formatMessage({
          id: 'team_members.create_edit_member.permissions_section.title'
        })}
      </div>
      <div className="br-permissions-section__content">
        {hasNoSelectedPermissions(selectedPermissions) && (
          <Alert
            className="br-permissions-section__error-alert"
            description={formatMessage({
              id: 'team_members.create_edit_member.permissions_section.empty_permissions_error_msg'
            })}
            icon={<ErrorAlert />}
            type="error"
            showIcon
          />
        )}
        <div className="br-permissions-section__table">
          <div className="br-permissions-section__table__header">
            <div>
              <Checkbox onChange={handleSelectAll} checked={isAllSelected} />
              <span className="option-item">
                {formatMessage({
                  id: 'team_members.create_edit_member.permissions_section.columns.module'
                })}
              </span>
            </div>
            {!isMediumMobileScreen && (
              <div className="br-permissions-section__table__options">
                <span className="option-item">
                  {formatMessage({
                    id: 'team_members.create_edit_member.permissions_section.columns.full_access'
                  })}
                </span>
                <span className="option-item">
                  {formatMessage({
                    id: 'team_members.create_edit_member.permissions_section.columns.editor'
                  })}
                </span>
                <span className="option-item">
                  {formatMessage({
                    id: 'team_members.create_edit_member.permissions_section.columns.view_only'
                  })}
                </span>
              </div>
            )}
          </div>
          <div>{PERMISSIONS_MODULES.map(renderItem)}</div>
        </div>
      </div>
    </div>
  );
};

export default mediaHook(PermissionsSection);
